/**
 * Permission Service
 * Manages roles and permissions for users across organizations
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { 
  Role, 
  RolePermissions, 
  PermissionLevel, 
  PageKey,
  hasPermission,
  PAGE_METADATA
} from '../types/permissions';
import { User } from '../types';

/**
 * Default permissions for system roles
 */
const ADMIN_PERMISSIONS: RolePermissions = {
  dashboard: 'create',
  'quick-select': 'create',
  stream: 'create',
  emails: 'create',
  tasks: 'create',
  'time-tracking': 'create',
  'expenses': 'create',
  'engagements': 'create',
  'team-skills': 'create',
  leads: 'create',
  companies: 'create',
  opportunities: 'create',
  customers: 'create',
  products: 'create',
  offers: 'create',
  documents: 'create',
  material: 'create',
  insights: 'create',
  'business-plan': 'create',
  automate: 'create',
  'google-ads-overview': 'create',
  'google-ads-campaigns': 'create',
  'google-ads-optimization': 'create',
  'google-ads-assets': 'create',
  'google-ads-templates': 'create',
  'google-ads-insights': 'create',
  'google-ads-merchant': 'create',
  'closing-assistant': 'create',
  'brandbook': 'create',
  'customer-intelligence': 'create',
  'company-profile': 'create',
  'echo-brain': 'create',
  settings: 'create',
  'software-compliance': 'create',
  'development': 'create',
};

const MANAGER_PERMISSIONS: RolePermissions = {
  dashboard: 'write',
  'quick-select': 'write',
  stream: 'write',
  emails: 'write',
  tasks: 'write',
  'time-tracking': 'write',
  'expenses': 'write',
  'engagements': 'write',
  'team-skills': 'write',
  leads: 'write',
  companies: 'write',
  opportunities: 'write',
  customers: 'write',
  products: 'write',
  offers: 'write',
  documents: 'write',
  material: 'write',
  insights: 'write',
  'business-plan': 'write',
  automate: 'write',
  'google-ads-overview': 'write',
  'google-ads-campaigns': 'write',
  'google-ads-optimization': 'write',
  'google-ads-assets': 'write',
  'google-ads-templates': 'write',
  'google-ads-insights': 'write',
  'google-ads-merchant': 'write',
  'closing-assistant': 'write',
  'brandbook': 'write',
  'customer-intelligence': 'write',
  'company-profile': 'write',
  'echo-brain': 'read',
  settings: 'read',
  'software-compliance': 'read',
  'development': 'write',
};

const SALES_REP_PERMISSIONS: RolePermissions = {
  dashboard: 'read',
  'quick-select': 'write',
  stream: 'read',
  emails: 'write',
  tasks: 'write',
  'time-tracking': 'write',
  'expenses': 'write',
  'engagements': 'write',
  'team-skills': 'write',
  leads: 'write',
  companies: 'read',
  opportunities: 'write',
  customers: 'read',
  products: 'read',
  offers: 'write',
  documents: 'write',
  material: 'read',
  insights: 'read',
  'business-plan': 'read',
  automate: 'read',
  'google-ads-overview': 'read',
  'google-ads-campaigns': 'read',
  'google-ads-optimization': 'read',
  'google-ads-assets': 'read',
  'google-ads-templates': 'read',
  'google-ads-insights': 'read',
  'google-ads-merchant': 'read',
  'closing-assistant': 'write',
  'brandbook': 'read',
  'customer-intelligence': 'read',
  'company-profile': 'read',
  'echo-brain': 'read',
  settings: 'none',
  'software-compliance': 'read',
  'development': 'write',
};

export const permissionService = {
  /**
   * Get default system roles for an organization
   */
  getDefaultSystemRoles(orgId: string, createdBy: string = 'system'): Role[] {
    const now = new Date();
    
    return [
      {
        id: `${orgId}_admin`,
        name: 'Admin',
        description: 'Full system access with all permissions',
        isSystemRole: true,
        permissions: ADMIN_PERMISSIONS,
        orgId,
        createdBy,
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${orgId}_manager`,
        name: 'Manager',
        description: 'Can manage most resources, limited settings access',
        isSystemRole: true,
        permissions: MANAGER_PERMISSIONS,
        orgId,
        createdBy,
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${orgId}_sales_rep`,
        name: 'Sales Rep',
        description: 'Can manage own sales activities, read-only for most other areas',
        isSystemRole: true,
        permissions: SALES_REP_PERMISSIONS,
        orgId,
        createdBy,
        createdAt: now,
        updatedAt: now,
      },
    ];
  },

  /**
   * Initialize system roles for an organization
   */
  async initializeSystemRoles(orgId: string, createdBy: string): Promise<void> {
    try {
      const systemRoles = this.getDefaultSystemRoles(orgId, createdBy);
      
      for (const role of systemRoles) {
        const roleRef = doc(db, 'roles', role.id);
        const roleSnap = await getDoc(roleRef);
        
        if (!roleSnap.exists()) {
          console.log(`Creating system role: ${role.name} for org ${orgId}`);
          await setDoc(roleRef, {
            ...role,
            createdAt: Timestamp.fromDate(role.createdAt),
            updatedAt: Timestamp.fromDate(role.updatedAt),
          });
        } else {
          // Check if existing role has all current page keys and update if needed
          await this.updateSystemRoleWithNewPages(roleSnap.id, roleSnap.data(), role.permissions);
        }
      }
    } catch (error) {
      console.error('Error initializing system roles:', error);
      throw error;
    }
  },

  /**
   * Update a system role with any new page keys that were added since the role was created
   */
  async updateSystemRoleWithNewPages(
    roleId: string, 
    existingData: any, 
    defaultPermissions: RolePermissions
  ): Promise<void> {
    try {
      const existingPermissions = existingData.permissions || {};
      const allPageKeys = PAGE_METADATA.map(p => p.key);
      
      // Find missing page keys
      const missingKeys = allPageKeys.filter(key => !(key in existingPermissions));
      
      if (missingKeys.length === 0) {
        return; // All page keys are present, no update needed
      }
      
      console.log(`Updating system role ${roleId} with missing page keys:`, missingKeys);
      
      // Merge existing permissions with defaults for missing keys
      const updatedPermissions: RolePermissions = { ...existingPermissions };
      for (const key of missingKeys) {
        updatedPermissions[key as PageKey] = defaultPermissions[key as PageKey];
      }
      
      // Update the role in Firestore
      const roleRef = doc(db, 'roles', roleId);
      await updateDoc(roleRef, {
        permissions: updatedPermissions,
        updatedAt: Timestamp.now(),
      });
      
      console.log(`Successfully updated system role ${roleId} with ${missingKeys.length} new page permissions`);
    } catch (error) {
      console.error('Error updating system role with new pages:', error);
      // Don't throw - this is a non-critical update
    }
  },

  /**
   * Get all roles for an organization (system + custom)
   */
  async getRolesForOrg(orgId: string): Promise<Role[]> {
    try {
      const rolesRef = collection(db, 'roles');
      const q = query(rolesRef, where('orgId', '==', orgId));
      const snapshot = await getDocs(q);
      
      const roles = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          isSystemRole: data.isSystemRole || false,
          permissions: data.permissions as RolePermissions,
          orgId: data.orgId,
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Role;
      });

      // Sort: system roles first, then custom roles
      return roles.sort((a, b) => {
        if (a.isSystemRole && !b.isSystemRole) return -1;
        if (!a.isSystemRole && b.isSystemRole) return 1;
        return a.name.localeCompare(b.name);
      });
    } catch (error) {
      console.error('Error getting roles:', error);
      return [];
    }
  },

  /**
   * Get a specific role by ID
   */
  async getRole(roleId: string): Promise<Role | null> {
    try {
      const roleRef = doc(db, 'roles', roleId);
      const roleSnap = await getDoc(roleRef);
      
      if (!roleSnap.exists()) {
        return null;
      }
      
      const data = roleSnap.data();
      return {
        id: roleSnap.id,
        name: data.name,
        description: data.description,
        isSystemRole: data.isSystemRole || false,
        permissions: data.permissions as RolePermissions,
        orgId: data.orgId,
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Role;
    } catch (error) {
      console.error('Error getting role:', error);
      return null;
    }
  },

  /**
   * Create a new custom role
   */
  async createCustomRole(
    roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      if (roleData.isSystemRole) {
        throw new Error('Cannot create system roles through this method');
      }

      const rolesRef = collection(db, 'roles');
      const docRef = await addDoc(rolesRef, {
        ...roleData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating custom role:', error);
      throw error;
    }
  },

  /**
   * Update a role (only custom roles can be updated)
   */
  async updateRole(roleId: string, updates: Partial<Role>): Promise<void> {
    try {
      const roleRef = doc(db, 'roles', roleId);
      const roleSnap = await getDoc(roleRef);
      
      if (!roleSnap.exists()) {
        throw new Error('Role not found');
      }
      
      const roleData = roleSnap.data();
      if (roleData.isSystemRole) {
        throw new Error('Cannot update system roles');
      }
      
      const allowedUpdates: any = {};
      if (updates.name) allowedUpdates.name = updates.name;
      if (updates.description !== undefined) allowedUpdates.description = updates.description;
      if (updates.permissions) allowedUpdates.permissions = updates.permissions;
      
      allowedUpdates.updatedAt = Timestamp.now();
      
      await updateDoc(roleRef, allowedUpdates);
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  },

  /**
   * Delete a custom role
   */
  async deleteCustomRole(roleId: string): Promise<void> {
    try {
      const roleRef = doc(db, 'roles', roleId);
      const roleSnap = await getDoc(roleRef);
      
      if (!roleSnap.exists()) {
        throw new Error('Role not found');
      }
      
      const roleData = roleSnap.data();
      if (roleData.isSystemRole) {
        throw new Error('Cannot delete system roles');
      }
      
      // Check if any users have this role assigned
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, where('customRoleId', '==', roleId));
      const usersSnap = await getDocs(usersQuery);
      
      if (!usersSnap.empty) {
        throw new Error(`Cannot delete role: ${usersSnap.size} user(s) are assigned to this role`);
      }
      
      await deleteDoc(roleRef);
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  },

  /**
   * Get effective permissions for a user
   */
  async getUserEffectivePermissions(user: User, orgId: string): Promise<RolePermissions> {
    try {
      let basePermissions: RolePermissions;
      
      // Get default permissions based on role
      switch (user.role) {
        case 'admin':
          basePermissions = { ...ADMIN_PERMISSIONS };
          break;
        case 'manager':
          basePermissions = { ...MANAGER_PERMISSIONS };
          break;
        case 'sales_rep':
          basePermissions = { ...SALES_REP_PERMISSIONS };
          break;
        default:
          basePermissions = { ...SALES_REP_PERMISSIONS };
      }
      
      // If user has a custom role, merge with that
      if (user.customRoleId) {
        const customRole = await this.getRole(user.customRoleId);
        if (customRole && customRole.orgId === orgId) {
          // Merge custom permissions with defaults (custom overrides defaults)
          return { ...basePermissions, ...customRole.permissions };
        }
      }
      
      // Try to load system role from Firestore
      const systemRoleId = `${orgId}_${user.role}`;
      const systemRole = await this.getRole(systemRoleId);
      
      if (systemRole && systemRole.permissions) {
        // Merge Firestore permissions with defaults (Firestore overrides defaults)
        return { ...basePermissions, ...systemRole.permissions };
      }
      // Fallback to default permissions
      return basePermissions;
    } catch (error) {
      console.error('Error getting user effective permissions:', error);
      // Return default permissions based on role on error
      switch (user.role) {
        case 'admin':
          return ADMIN_PERMISSIONS;
        case 'manager':
          return MANAGER_PERMISSIONS;
        default:
          return SALES_REP_PERMISSIONS;
      }
    }
  },

  /**
   * Check if user has required permission level for a page
   */
  async checkPermission(
    user: User,
    page: PageKey,
    requiredLevel: PermissionLevel,
    orgId: string
  ): Promise<boolean> {
    try {
      const permissions = await this.getUserEffectivePermissions(user, orgId);
      const userLevel = permissions[page];
      return hasPermission(userLevel, requiredLevel);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  },

  /**
   * Assign a role to a user
   */
  async assignRoleToUser(userId: string, roleId: string | null): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      
      if (roleId === null) {
        // Remove custom role assignment
        await updateDoc(userRef, {
          customRoleId: null,
          updatedAt: Timestamp.now(),
        });
        return;
      }
      
      // Verify role exists
      const role = await this.getRole(roleId);
      if (!role) {
        throw new Error('Role not found');
      }
      
      // If it's a system role, update the user.role field instead
      if (role.isSystemRole) {
        let systemRole: 'admin' | 'manager' | 'sales_rep';
        if (role.id.endsWith('_admin')) {
          systemRole = 'admin';
        } else if (role.id.endsWith('_manager')) {
          systemRole = 'manager';
        } else {
          systemRole = 'sales_rep';
        }
        
        await updateDoc(userRef, {
          role: systemRole,
          customRoleId: null,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Custom role - keep existing system role, add custom role reference
        await updateDoc(userRef, {
          customRoleId: roleId,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error assigning role to user:', error);
      throw error;
    }
  },

  /**
   * Get users assigned to a specific role
   */
  async getUsersWithRole(roleId: string): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, where('customRoleId', '==', roleId));
      const snapshot = await getDocs(usersQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as User));
    } catch (error) {
      console.error('Error getting users with role:', error);
      return [];
    }
  },

  /**
   * Bulk update permissions for a role
   */
  async updateRolePermissions(roleId: string, permissions: RolePermissions): Promise<void> {
    try {
      const roleRef = doc(db, 'roles', roleId);
      const roleSnap = await getDoc(roleRef);
      
      if (!roleSnap.exists()) {
        throw new Error('Role not found');
      }
      
      const roleData = roleSnap.data();
      if (roleData.isSystemRole) {
        throw new Error('Cannot update system role permissions');
      }
      
      await updateDoc(roleRef, {
        permissions,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating role permissions:', error);
      throw error;
    }
  },
};

